// Service de base de données SQLite pour Desktop
// Version compatible navigateur (localStorage) et Electron (SQLite)

// Import de l'utilitaire de détection Electron
import { isElectron as checkElectron } from '../utils/electron';

// Détection de l'environnement
const isElectron = checkElectron;

// Import dynamique pour Electron (évite que Webpack bundlise ces modules)
// Utilisation de window.require qui n'est pas analysé par Webpack
const getElectronModules = () => {
  if (!isElectron()) {
    return null;
  }
  
  try {
    // Utiliser window.require qui est disponible dans Electron renderer
    // et qui n'est PAS analysé par Webpack (évite les erreurs de bundling)
    if (typeof window === 'undefined' || !window.require) {
      return null;
    }

    const requireFunc = window.require;

    // Essayer de charger better-sqlite3, mais ne pas échouer si absent
    let sqlite3 = null;
    try {
      sqlite3 = requireFunc('better-sqlite3');
    } catch (sqliteError) {
      console.warn('better-sqlite3 non disponible, utilisation de localStorage:', sqliteError.message);
      // Continuer sans sqlite3
    }

    return {
      sqlite3: sqlite3,
      electron: requireFunc('electron'),
      path: requireFunc('path')
    };
  } catch (e) {
    console.warn('Electron modules not available, using browser fallback:', e);
    return null;
  }
};

class DatabaseService {
  constructor() {
    this.db = null;
    this.dbPath = null;
    this.modules = null;
    this.isElectronEnv = false;
  }

  async init() {
    // Si on n'est pas dans Electron, utiliser le service navigateur
    if (!isElectron()) {
      console.log('Mode navigateur: utilisation de localStorage');
      return Promise.resolve();
    }

    // Charger les modules Electron dynamiquement
    this.modules = getElectronModules();
    if (!this.modules) {
      console.log('Modules Electron non disponibles, utilisation de localStorage');
      return Promise.resolve();
    }

    const { sqlite3, electron, path } = this.modules;
    
    // Si sqlite3 n'est pas disponible, utiliser localStorage
    if (!sqlite3) {
      console.log('sqlite3 non disponible, utilisation de localStorage');
      return Promise.resolve();
    }
    
    const app = electron.remote ? electron.remote.app : electron.app;
    this.isElectronEnv = true;

    try {
      // Déterminer le chemin de la base de données
      const userDataPath = app.getPath('userData');
      this.dbPath = path.join(userDataPath, 'goldflow.db');

      // better-sqlite3 est synchrone, pas besoin de callback
      this.db = new sqlite3(this.dbPath);
      console.log('Base de données connectée:', this.dbPath);
      
      // Créer les tables
      await this.createTables();
      return Promise.resolve();
    } catch (error) {
      console.error('Erreur initialisation Electron (better-sqlite3):', error);
      // Ne pas rejeter, utiliser localStorage en fallback
      console.log('Fallback sur localStorage');
      return Promise.resolve();
    }
  }

  async createTables() {
    return new Promise((resolve, reject) => {
      const queries = [
        `CREATE TABLE IF NOT EXISTS transactions (
          id INTEGER PRIMARY KEY AUTOINCREMENT,
          transaction_id TEXT UNIQUE NOT NULL,
          type TEXT NOT NULL,
          amount REAL NOT NULL,
          currency TEXT NOT NULL DEFAULT 'USD',
          amount_aed REAL,
          amount_usd REAL,
          location TEXT NOT NULL,
          partner_id INTEGER,
          gold_price_executable REAL,
          gold_price_indicative REAL,
          carat_gross REAL,
          carat_net REAL,
          weight_gross REAL,
          weight_net REAL,
          loss_rate REAL,
          margin REAL,
          profit REAL,
          status TEXT DEFAULT 'draft',
          is_locked INTEGER DEFAULT 0,
          created_by INTEGER,
          validated_by INTEGER,
          validated_at TEXT,
          created_at TEXT DEFAULT CURRENT_TIMESTAMP,
          updated_at TEXT DEFAULT CURRENT_TIMESTAMP,
          deleted_at TEXT,
          synced INTEGER DEFAULT 0
        )`,
        `CREATE TABLE IF NOT EXISTS open_positions (
          id INTEGER PRIMARY KEY AUTOINCREMENT,
          transaction_id INTEGER NOT NULL,
          weighted_avg_price REAL NOT NULL,
          quantity REAL NOT NULL,
          current_price REAL,
          latent_pnl REAL,
          total_exposure REAL,
          location TEXT NOT NULL,
          type TEXT NOT NULL,
          carat REAL,
          created_at TEXT DEFAULT CURRENT_TIMESTAMP,
          updated_at TEXT DEFAULT CURRENT_TIMESTAMP
        )`,
        `CREATE TABLE IF NOT EXISTS physical_stock (
          id INTEGER PRIMARY KEY AUTOINCREMENT,
          location TEXT NOT NULL,
          type TEXT NOT NULL,
          carat REAL,
          quantity_theoretical REAL NOT NULL DEFAULT 0,
          quantity_real REAL NOT NULL DEFAULT 0,
          value_theoretical REAL,
          value_real REAL,
          last_inventory_date TEXT,
          alert_threshold REAL DEFAULT 5.0,
          created_at TEXT DEFAULT CURRENT_TIMESTAMP,
          updated_at TEXT DEFAULT CURRENT_TIMESTAMP
        )`,
        `CREATE TABLE IF NOT EXISTS gold_price_history (
          id INTEGER PRIMARY KEY AUTOINCREMENT,
          price_indicative REAL NOT NULL,
          price_executable REAL,
          source TEXT,
          timestamp TEXT DEFAULT CURRENT_TIMESTAMP
        )`,
        `CREATE INDEX IF NOT EXISTS idx_transactions_type ON transactions(type)`,
        `CREATE INDEX IF NOT EXISTS idx_transactions_status ON transactions(status)`,
        `CREATE INDEX IF NOT EXISTS idx_transactions_location ON transactions(location)`,
        `CREATE INDEX IF NOT EXISTS idx_transactions_created_at ON transactions(created_at)`
      ];

      let completed = 0;
      queries.forEach(query => {
        this.db.run(query, (err) => {
          if (err) {
            console.error('Erreur création table:', err);
            reject(err);
          } else {
            completed++;
            if (completed === queries.length) {
              resolve();
            }
          }
        });
      });
    });
  }

  async close() {
    return new Promise((resolve, reject) => {
      if (this.db) {
        this.db.close((err) => {
          if (err) {
            reject(err);
          } else {
            resolve();
          }
        });
      } else {
        resolve();
      }
    });
  }

  // Transactions
  async createTransaction(transaction) {
    if (!this.isElectronEnv || !this.db) {
      throw new Error('Database not initialized. Use BrowserDatabaseService in browser mode.');
    }

    return new Promise((resolve, reject) => {
      const {
        transaction_id, type, amount, currency, location, partner_id,
        gold_price_executable, gold_price_indicative, carat_gross, carat_net,
        weight_gross, weight_net, created_by
      } = transaction;

      // Calculs automatiques
      const amount_usd = currency === 'USD' ? amount : amount / 3.67;
      const amount_aed = currency === 'AED' ? amount : amount * 3.67;
      const loss_rate = weight_gross && weight_net 
        ? ((weight_gross - weight_net) / weight_gross) * 100 
        : null;

      const sql = `INSERT INTO transactions (
        transaction_id, type, amount, currency, amount_aed, amount_usd,
        location, partner_id, gold_price_executable, gold_price_indicative,
        carat_gross, carat_net, weight_gross, weight_net, loss_rate,
        created_by, synced
      ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, 0)`;

      this.db.run(sql, [
        transaction_id, type, amount, currency, amount_aed, amount_usd,
        location, partner_id || null, gold_price_executable || null,
        gold_price_indicative || null, carat_gross || null, carat_net || null,
        weight_gross || null, weight_net || null, loss_rate, created_by || 1
      ], function(err) {
        if (err) {
          reject(err);
        } else {
          resolve(this.lastID);
        }
      });
    });
  }

  async getTransactions(filters = {}) {
    return new Promise((resolve, reject) => {
      let sql = 'SELECT * FROM transactions WHERE deleted_at IS NULL';
      const params = [];

      if (filters.status) {
        if (filters.status === 'closed') {
          sql += ' AND status = ?';
          params.push('closed');
        } else if (filters.status === 'open') {
          sql += ' AND status != ?';
          params.push('closed');
        }
      }

      if (filters.searchTerm && filters.searchType) {
        if (filters.searchType === 'transactionId') {
          sql += ' AND transaction_id LIKE ?';
          params.push(`%${filters.searchTerm}%`);
        } else if (filters.searchType === 'amount') {
          sql += ' AND amount LIKE ?';
          params.push(`%${filters.searchTerm}%`);
        }
      }

      sql += ' ORDER BY created_at DESC';

      this.db.all(sql, params, (err, rows) => {
        if (err) {
          reject(err);
        } else {
          resolve(rows);
        }
      });
    });
  }

  async getTransactionById(id) {
    return new Promise((resolve, reject) => {
      this.db.get('SELECT * FROM transactions WHERE id = ?', [id], (err, row) => {
        if (err) {
          reject(err);
        } else {
          resolve(row);
        }
      });
    });
  }

  async updateTransaction(id, updates) {
    return new Promise((resolve, reject) => {
      const fields = [];
      const values = [];

      Object.keys(updates).forEach(key => {
        fields.push(`${key} = ?`);
        values.push(updates[key]);
      });

      fields.push('updated_at = CURRENT_TIMESTAMP');
      values.push(id);

      const sql = `UPDATE transactions SET ${fields.join(', ')} WHERE id = ?`;

      this.db.run(sql, values, function(err) {
        if (err) {
          reject(err);
        } else {
          resolve(this.changes);
        }
      });
    });
  }

  async deleteTransaction(id) {
    return new Promise((resolve, reject) => {
      // Soft delete : marquer comme supprimé au lieu de supprimer réellement
      const sql = `UPDATE transactions SET deleted_at = CURRENT_TIMESTAMP WHERE id = ?`;

      this.db.run(sql, [id], function(err) {
        if (err) {
          reject(err);
        } else {
          resolve(this.changes);
        }
      });
    });
  }

  // Positions ouvertes
  async getOpenPositions(filters = {}) {
    return new Promise((resolve, reject) => {
      let sql = `SELECT t.*, op.* 
        FROM transactions t
        LEFT JOIN open_positions op ON op.transaction_id = t.id
        WHERE t.status != 'closed' AND t.type = 'buy' AND t.deleted_at IS NULL`;

      const params = [];

      if (filters.location && filters.location !== 'all') {
        sql += ' AND t.location = ?';
        params.push(filters.location);
      }

      if (filters.type && filters.type !== 'all') {
        sql += ' AND op.type = ?';
        params.push(filters.type);
      }

      sql += ' ORDER BY t.created_at DESC';

      this.db.all(sql, params, (err, rows) => {
        if (err) {
          reject(err);
        } else {
          resolve(rows);
        }
      });
    });
  }

  async calculateOpenPositionsStats() {
    return new Promise((resolve, reject) => {
      const sql = `SELECT 
        AVG(t.amount_usd / t.weight_net) as weighted_avg_price,
        SUM(t.weight_net) as total_quantity,
        SUM(t.amount_usd) as total_exposure
      FROM transactions t
      WHERE t.status != 'closed' AND t.type = 'buy' AND t.deleted_at IS NULL AND t.weight_net > 0`;

      this.db.get(sql, [], (err, row) => {
        if (err) {
          reject(err);
        } else {
          resolve(row || { weighted_avg_price: 0, total_quantity: 0, total_exposure: 0 });
        }
      });
    });
  }

  // Stock
  async getStock(filters = {}) {
    return new Promise((resolve, reject) => {
      let sql = 'SELECT * FROM physical_stock WHERE 1=1';
      const params = [];

      if (filters.location) {
        sql += ' AND location = ?';
        params.push(filters.location);
      }

      if (filters.type) {
        sql += ' AND type = ?';
        params.push(filters.type);
      }

      this.db.all(sql, params, (err, rows) => {
        if (err) {
          reject(err);
        } else {
          resolve(rows);
        }
      });
    });
  }

  async updateStockReal(location, type, quantity_real) {
    return new Promise((resolve, reject) => {
      const sql = `UPDATE physical_stock 
        SET quantity_real = ?, last_inventory_date = CURRENT_TIMESTAMP, updated_at = CURRENT_TIMESTAMP
        WHERE location = ? AND type = ?`;

      this.db.run(sql, [quantity_real, location, type], function(err) {
        if (err) {
          reject(err);
        } else {
          resolve(this.changes);
        }
      });
    });
  }

  // Marges et profits
  async getMarginsData(month = null) {
    return new Promise((resolve, reject) => {
      let sql = `SELECT 
        SUM(CASE WHEN type = 'buy' THEN amount_usd ELSE 0 END) as total_achete,
        SUM(CASE WHEN type = 'sell' THEN amount_usd ELSE 0 END) as total_vendu,
        SUM(CASE WHEN type = 'sell' THEN profit ELSE 0 END) as total_profit
      FROM transactions
      WHERE deleted_at IS NULL AND status = 'closed'`;

      const params = [];

      if (month) {
        sql += ' AND strftime("%Y-%m", created_at) = ?';
        params.push(month);
      }

      this.db.get(sql, params, (err, row) => {
        if (err) {
          reject(err);
        } else {
          resolve(row || { total_achete: 0, total_vendu: 0, total_profit: 0 });
        }
      });
    });
  }

  // Sync
  async getPendingSync() {
    if (!this.isElectronEnv || !this.db) {
      // Fallback sur BrowserDatabaseService
      return [];
    }
    
    try {
      // better-sqlite3 utilise une API synchrone
      const stmt = this.db.prepare('SELECT * FROM transactions WHERE synced = 0 AND deleted_at IS NULL');
      const rows = stmt.all();
      return rows;
    } catch (error) {
      console.error('Erreur getPendingSync:', error);
      return [];
    }
  }

  async markAsSynced(ids) {
    return new Promise((resolve, reject) => {
      const placeholders = ids.map(() => '?').join(',');
      const sql = `UPDATE transactions SET synced = 1 WHERE id IN (${placeholders})`;

      this.db.run(sql, ids, function(err) {
        if (err) {
          reject(err);
        } else {
          resolve(this.changes);
        }
      });
    });
  }

  // Gold Price
  async saveGoldPrice(price_indicative, price_executable = null, source = null) {
    return new Promise((resolve, reject) => {
      const sql = `INSERT INTO gold_price_history (price_indicative, price_executable, source) 
        VALUES (?, ?, ?)`;

      this.db.run(sql, [price_indicative, price_executable, source], function(err) {
        if (err) {
          reject(err);
        } else {
          resolve(this.lastID);
        }
      });
    });
  }

  async getGoldPriceHistory(days = 7) {
    return new Promise((resolve, reject) => {
      const sql = `SELECT price_indicative, timestamp 
        FROM gold_price_history 
        WHERE timestamp >= datetime('now', '-${days} days')
        ORDER BY timestamp ASC`;

      this.db.all(sql, [], (err, rows) => {
        if (err) {
          reject(err);
        } else {
          resolve(rows);
        }
      });
    });
  }
}

// Version navigateur avec localStorage (fallback pour développement web)
class BrowserDatabaseService {
    constructor() {
      this.storageKey = 'goldflow_db';
      this.init();
    }

    init() {
      if (!localStorage.getItem(this.storageKey)) {
        localStorage.setItem(this.storageKey, JSON.stringify({
          transactions: [],
          gold_price_history: []
        }));
      }
    }

    async createTransaction(transaction) {
      const data = JSON.parse(localStorage.getItem(this.storageKey));
      const id = data.transactions.length + 1;
      data.transactions.push({ id, ...transaction, created_at: new Date().toISOString() });
      localStorage.setItem(this.storageKey, JSON.stringify(data));
      return id;
    }

    async getTransactions(filters = {}) {
      const data = JSON.parse(localStorage.getItem(this.storageKey));
      let transactions = data.transactions || [];

      if (filters.status === 'closed') {
        transactions = transactions.filter(t => t.status === 'closed');
      } else if (filters.status === 'open') {
        transactions = transactions.filter(t => t.status !== 'closed');
      }

      return transactions;
    }

    async getOpenPositions() {
      const data = JSON.parse(localStorage.getItem(this.storageKey));
      return (data.transactions || []).filter(t => t.type === 'buy' && t.status !== 'closed');
    }

    async getStock() {
      return [];
    }

    async getMarginsData() {
      const data = JSON.parse(localStorage.getItem(this.storageKey));
      const transactions = data.transactions || [];
      const total_achete = transactions.filter(t => t.type === 'buy').reduce((sum, t) => sum + (t.amount_usd || 0), 0);
      const total_vendu = transactions.filter(t => t.type === 'sell').reduce((sum, t) => sum + (t.amount_usd || 0), 0);
      return { total_achete, total_vendu, total_profit: total_vendu - total_achete };
    }

    async saveGoldPrice(price_indicative) {
      const data = JSON.parse(localStorage.getItem(this.storageKey));
      if (!data.gold_price_history) data.gold_price_history = [];
      data.gold_price_history.push({
        price_indicative,
        timestamp: new Date().toISOString()
      });
      // Garder seulement les 7 derniers jours
      if (data.gold_price_history.length > 100) {
        data.gold_price_history = data.gold_price_history.slice(-100);
      }
      localStorage.setItem(this.storageKey, JSON.stringify(data));
    }

    async getGoldPriceHistory(days = 7) {
      const data = JSON.parse(localStorage.getItem(this.storageKey));
      const history = data.gold_price_history || [];
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - days);
      return history.filter(h => new Date(h.timestamp) >= cutoff).map(h => ({
        price_indicative: h.price_indicative,
        timestamp: h.timestamp
      }));
    }

    async getPendingSync() {
      const data = JSON.parse(localStorage.getItem(this.storageKey));
      return (data.transactions || []).filter(t => !t.synced);
    }

    async markAsSynced(ids) {
      const data = JSON.parse(localStorage.getItem(this.storageKey));
      data.transactions = data.transactions.map(t => 
        ids.includes(t.id) ? { ...t, synced: true } : t
      );
      localStorage.setItem(this.storageKey, JSON.stringify(data));
      return ids.length;
    }

    async calculateOpenPositionsStats() {
      const data = JSON.parse(localStorage.getItem(this.storageKey));
      const positions = (data.transactions || []).filter(t => t.type === 'buy' && t.status !== 'closed' && t.weight_net > 0);
      
      if (positions.length === 0) {
        return { weighted_avg_price: 0, total_quantity: 0, total_exposure: 0 };
      }

      const totalWeight = positions.reduce((sum, p) => sum + (p.weight_net || 0), 0);
      const totalAmount = positions.reduce((sum, p) => sum + (p.amount_usd || 0), 0);
      const avgPrice = totalWeight > 0 ? totalAmount / totalWeight : 0;

      return {
        weighted_avg_price: avgPrice,
        total_quantity: totalWeight,
        total_exposure: totalAmount
      };
    }

    async deleteTransaction(id) {
      const data = JSON.parse(localStorage.getItem(this.storageKey));
      data.transactions = data.transactions.filter(t => t.id !== id);
      localStorage.setItem(this.storageKey, JSON.stringify(data));
      return 1;
    }

    async updateTransaction(id, updates) {
      const data = JSON.parse(localStorage.getItem(this.storageKey));
      const index = data.transactions.findIndex(t => t.id === id);
      if (index !== -1) {
        data.transactions[index] = { ...data.transactions[index], ...updates, updated_at: new Date().toISOString() };
        localStorage.setItem(this.storageKey, JSON.stringify(data));
        return 1;
      }
      return 0;
    }

    async getTransactionById(id) {
      const data = JSON.parse(localStorage.getItem(this.storageKey));
      return data.transactions.find(t => t.id === id) || null;
    }
  }

// Créer l'instance appropriée selon l'environnement
let databaseInstance = null;
let isInitialized = false;
let initPromise = null; // Pour éviter les initialisations multiples simultanées

// Fonction pour initialiser la base de données selon l'environnement
async function initializeDatabase() {
  // Si déjà initialisé, retourner l'instance
  if (isInitialized && databaseInstance) {
    return databaseInstance;
  }
  
  // Si une initialisation est en cours, attendre qu'elle se termine
  if (initPromise) {
    return initPromise;
  }
  
  // Créer une nouvelle promesse d'initialisation
  initPromise = (async () => {
    try {
      // Si on est en Electron, utiliser SQLite
      if (isElectron()) {
        try {
          const sqliteInstance = new DatabaseService();
          await sqliteInstance.init();
          databaseInstance = sqliteInstance;
          isInitialized = true;
          console.log('✅ Base de données SQLite initialisée (mode Electron)');
          return databaseInstance;
        } catch (error) {
          console.warn('⚠️ Erreur initialisation SQLite, fallback sur localStorage:', error);
          // Fallback sur localStorage si SQLite échoue
          databaseInstance = new BrowserDatabaseService();
          isInitialized = true;
          return databaseInstance;
        }
      } else {
        // Mode navigateur : utiliser localStorage (synchrone, rapide)
        databaseInstance = new BrowserDatabaseService();
        isInitialized = true;
        console.log('✅ Base de données localStorage initialisée (mode navigateur)');
        return databaseInstance;
      }
    } finally {
      // Réinitialiser la promesse une fois terminée
      initPromise = null;
    }
  })();
  
  return initPromise;
}

// Instance par défaut (sera remplacée après init)
// Utiliser BrowserDatabaseService temporairement pour éviter les erreurs
const defaultInstance = new BrowserDatabaseService();

// Export avec initialisation automatique
const database = {
  // Méthodes proxy qui délèguent à l'instance appropriée
  async init() {
    const instance = await initializeDatabase();
    return instance;
  },
  
  // Toutes les méthodes dupliquées pour compatibilité
  async createTransaction(...args) {
    const instance = await initializeDatabase();
    return instance.createTransaction(...args);
  },
  
  async getTransactions(...args) {
    const instance = await initializeDatabase();
    return instance.getTransactions(...args);
  },
  
  async getTransactionById(...args) {
    const instance = await initializeDatabase();
    return instance.getTransactionById(...args);
  },
  
  async updateTransaction(...args) {
    const instance = await initializeDatabase();
    return instance.updateTransaction(...args);
  },
  
  async deleteTransaction(...args) {
    const instance = await initializeDatabase();
    return instance.deleteTransaction(...args);
  },
  
  async getOpenPositions(...args) {
    const instance = await initializeDatabase();
    return instance.getOpenPositions(...args);
  },
  
  async calculateOpenPositionsStats(...args) {
    const instance = await initializeDatabase();
    return instance.calculateOpenPositionsStats(...args);
  },
  
  async getStock(...args) {
    const instance = await initializeDatabase();
    return instance.getStock(...args);
  },
  
  async updateStockReal(...args) {
    const instance = await initializeDatabase();
    return instance.updateStockReal(...args);
  },
  
  async getMarginsData(...args) {
    const instance = await initializeDatabase();
    return instance.getMarginsData(...args);
  },
  
  async getPendingSync(...args) {
    const instance = await initializeDatabase();
    return instance.getPendingSync(...args);
  },
  
  async markAsSynced(...args) {
    const instance = await initializeDatabase();
    return instance.markAsSynced(...args);
  },
  
  async saveGoldPrice(...args) {
    const instance = await initializeDatabase();
    return instance.saveGoldPrice(...args);
  },
  
  async getGoldPriceHistory(...args) {
    const instance = await initializeDatabase();
    return instance.getGoldPriceHistory(...args);
  },
  
  async close() {
    if (databaseInstance && databaseInstance.close) {
      return databaseInstance.close();
    }
    return Promise.resolve();
  }
};

// Ne pas initialiser automatiquement au chargement
// L'initialisation sera faite par DatabaseInitializer au démarrage de l'app

export default database;
